<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): see CriterionEnumInput.vue --
         inheritAttrs: false stops Vue 3 from auto-applying class/style to root. -->
    <div class="CriterionRadioSelectInput" :class="$attrs.class" :style="$attrs.style">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionRadioSelectInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <RadioOption
                    v-for="(option, i) in options"
                    :key="i"
                    :name="id"
                    :value="option.value"
                    :checked="value === option.value"
                    :disabled="disabled || rbacDisabled"
                    class="CriterionRadioSelectInput__option"
                    @input="onInput(option)"
                >
                    {{ option.label }}
                </RadioOption>
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template v-if="deletable || rbacDisabled" #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<style lang="scss">
    .CriterionRadioSelectInput {
        .FormInput__input {
            display: flex;
            gap: 16px;
        }
    }
</style>

<script lang="ts">
    import { Component, Watch } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { RadioOption } from '@evidentid/dashboard-commons/components/RadioOption';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';

    import AbstractCriterionInput from './AbstractCriterionInput';

    @Component({
        emits: [ 'delete', 'input' ],
        components: { FormInput, RadioOption, EidIcon, FontAwesomeIcon, FormElement, LockInputIcon, NewButton },
        inheritAttrs: false,
    })
    export default class CriterionRadioSelectInput extends AbstractCriterionInput<JsonFormBase<JsonSchemaEnum>, any> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;
        private options: DropdownOption[] = [];

        @Watch('form.schema.enum', { immediate: true })
        private onEnumChange(): void {
            const labels = this.form.schema.enumLabels && this.form.schema.enumLabels.length > 0
                ? this.form.schema.enumLabels
                : null;
            this.options = labels
                ? this.form.schema.enum.map((value, i) => ({ value, label: labels[i] }))
                : this.form.schema.enum.map(this.buildOptionFromValue);
        }

        private buildOptionFromValue(value: any): DropdownOption {
            return { value, label: value };
        }

        private onInput(option: DropdownOption): void {
            this.$emit('input', option.value);
        }
    }
</script>
